import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ArrowUp } from 'lucide-react';
import { ask, warmUp, CONTACT, SUGGESTIONS } from '../lib/bot';
import WhatsAppIcon from './WhatsAppIcon';

const WELCOME = {
  role: 'assistant',
  content:
    'Hi, I am the Farwings assistant. Ask me about our services, clients and reviews, or tell me where to take you, like "show me Sabta" or "take me to the process".',
};

function Bubble({ m, onNavigate }) {
  const mine = m.role === 'user';
  return (
    <div className={`flex ${mine ? 'justify-end' : 'justify-start'}`}>
      <div
        className={`max-w-[86%] whitespace-pre-line break-words rounded-[1.25rem] px-4 py-2.5 text-[0.9375rem] leading-relaxed ${
          mine ? 'rounded-br-md bg-[#4FB0E7] text-[#07101F]' : 'rounded-bl-md bg-white/[0.08] text-white'
        }`}
      >
        {m.content}
        {(m.links?.length || m.whatsapp) && (
          <div className="mt-3 flex flex-wrap gap-2">
            {m.links?.map((l) =>
              l.href ? (
                <a
                  key={l.href}
                  href={l.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="rounded-full border border-white/25 px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-white/10"
                >
                  {l.label} &#8599;
                </a>
              ) : (
                <button
                  key={l.to + l.label}
                  type="button"
                  onClick={() => onNavigate(l)}
                  className="rounded-full bg-white px-3 py-1.5 text-xs font-semibold text-[#07101F] transition-colors hover:bg-[#4FB0E7]"
                >
                  {l.label}
                </button>
              )
            )}
            {m.whatsapp && (
              <a
                href={CONTACT.whatsapp}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 rounded-full bg-[#25D366] px-3 py-1.5 text-xs font-semibold text-[#07101F]"
              >
                <WhatsAppIcon size={14} />
                WhatsApp us
              </a>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default function ChatBot() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([WELCOME]);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [aiOn, setAiOn] = useState(false);
  const listRef = useRef(null);
  const inputRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();

  // take the visitor somewhere on the site: a page, a section anchor, or a client on the ring
  const goTo = ({ to, client }, closeChat) => {
    if (!to) return;
    const [path, hash] = to.split('#');
    const page = path || '/';
    const samePage = location.pathname === page;
    if (closeChat || window.innerWidth < 640) setOpen(false); // phones: get the chat out of the way
    if (samePage && hash) {
      const el = document.getElementById(hash);
      if (el) {
        if (window.lenis) window.lenis.scrollTo(el, { offset: -80, duration: 1.2 });
        else el.scrollIntoView({ behavior: 'smooth' });
      }
    } else {
      navigate(to);
    }
    if (client != null) {
      setTimeout(() => window.dispatchEvent(new CustomEvent('fw:client', { detail: { id: client } })), samePage ? 1300 : 2000);
    }
  };

  useEffect(() => {
    if (open) {
      warmUp();
      setTimeout(() => inputRef.current?.focus(), 250);
    }
  }, [open]);

  useEffect(() => {
    const el = listRef.current;
    if (el) el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
  }, [messages, busy]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => e.key === 'Escape' && setOpen(false);
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  const send = async (text) => {
    const q = text.trim();
    if (!q || busy) return;
    const next = [...messages, { role: 'user', content: q }];
    setMessages(next);
    setInput('');
    setBusy(true);
    const history = next.filter((m) => m !== WELCOME).map(({ role, content }) => ({ role, content }));
    const reply = await ask(history);
    if (reply.source === 'ai' || reply.source === 'model') setAiOn(true);
    setMessages((cur) => [...cur, { role: 'assistant', content: reply.text, links: reply.links, whatsapp: reply.whatsapp }]);
    setBusy(false);
    if (reply.go) setTimeout(() => goTo(reply.go, false), 450); // let them read the reply first
  };

  const fresh = messages.length === 1;

  return (
    <div data-liquid-ignore className="pointer-events-none fixed bottom-4 right-4 z-[70] flex w-[min(24rem,calc(100vw-2rem))] flex-col items-end gap-3 sm:bottom-6 sm:right-6 [&>*]:pointer-events-auto">
      <AnimatePresence>
        {open && (
          <motion.section
            role="dialog"
            aria-label="Farwings assistant"
            initial={{ opacity: 0, y: 16, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 12, scale: 0.97 }}
            transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
            className="flex h-[min(34rem,calc(100dvh-12rem))] w-full origin-bottom-right flex-col overflow-hidden rounded-[1.75rem] bg-[#07101F]/95 text-white shadow-[0_30px_80px_-20px_rgba(0,0,0,0.7)] ring-1 ring-white/10 backdrop-blur-xl sm:w-[24rem]"
          >
            <header className="flex items-center gap-3 border-b border-white/15 px-5 py-4">
              <img src="/farwings-mark.png" alt="" className="h-9 w-9 object-contain" />
              <div className="flex-1">
                <p className="display-semi text-lg leading-tight">Farwings assistant</p>
                <p className="flex items-center gap-1.5 text-xs text-[#A6A6A6]">
                  <span className="h-1.5 w-1.5 rounded-full bg-[#3DDC97]" aria-hidden="true" />
                  {aiOn ? 'AI assistant, replies instantly' : 'Replies instantly'}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setOpen(false)}
                aria-label="Close assistant"
                className="flex h-9 w-9 items-center justify-center rounded-full bg-white/10 transition-colors hover:bg-white/20"
              >
                <X size={16} />
              </button>
            </header>

            <div ref={listRef} data-lenis-prevent aria-live="polite" className="flex-1 space-y-3 overflow-y-auto px-4 py-4">
              {messages.map((m, i) => (
                <Bubble key={i} m={m} onNavigate={(l) => goTo(l, true)} />
              ))}
              {busy && (
                <div className="flex gap-1 px-2 py-2" aria-label="Assistant is typing">
                  {[0, 1, 2].map((d) => (
                    <span
                      key={d}
                      className="h-2 w-2 animate-bounce rounded-full bg-white/50"
                      style={{ animationDelay: `${d * 0.15}s` }}
                    />
                  ))}
                </div>
              )}
              {fresh && (
                <div className="flex flex-wrap gap-2 pt-1">
                  {SUGGESTIONS.map((s) => (
                    <button
                      key={s}
                      type="button"
                      onClick={() => send(s)}
                      className="rounded-full border border-white/15 px-3 py-1.5 text-sm text-white/90 transition-colors hover:border-[#4FB0E7] hover:text-white"
                    >
                      {s}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <form
              onSubmit={(e) => {
                e.preventDefault();
                send(input);
              }}
              className="flex items-center gap-2 border-t border-white/15 p-3"
            >
              <label htmlFor="bot-input" className="sr-only">
                Your question
              </label>
              <input
                id="bot-input"
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Ask about your project…"
                autoComplete="off"
                maxLength={600}
                className="h-11 flex-1 rounded-full bg-white/[0.08] px-4 text-[0.9375rem] text-white placeholder:text-white/60 focus:outline-none focus:ring-2 focus:ring-[#4FB0E7]"
              />
              <button
                type="submit"
                disabled={!input.trim() || busy}
                aria-label="Send"
                className="flex h-11 w-11 items-center justify-center rounded-full bg-[#4FB0E7] text-[#07101F] transition-opacity disabled:opacity-40"
              >
                <ArrowUp size={18} />
              </button>
            </form>
          </motion.section>
        )}
      </AnimatePresence>

      <div className="flex flex-col items-end gap-3">
        <a
          href={CONTACT.whatsapp}
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Chat with Farwings on WhatsApp"
          className="flex h-14 w-14 items-center justify-center rounded-full bg-[#25D366] text-white shadow-[0_14px_30px_-10px_rgba(0,0,0,0.5)] transition-transform hover:-translate-y-0.5"
        >
          <WhatsAppIcon size={28} />
        </a>
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          aria-label={open ? 'Close assistant' : 'Open the Farwings assistant'}
          className="flex h-14 items-center gap-2.5 rounded-full bg-[#07101F] pl-2 pr-5 text-sm font-semibold text-white shadow-[0_14px_30px_-10px_rgba(0,0,0,0.6)] ring-1 ring-white/15 transition-transform hover:-translate-y-0.5"
        >
          <span className="flex h-10 w-10 items-center justify-center rounded-full bg-white/10">
            {open ? <X size={18} /> : <img src="/farwings-mark.png" alt="" className="h-7 w-7 object-contain" />}
          </span>
          <span className="hidden sm:inline">{open ? 'Close' : 'Ask Farwings'}</span>
        </button>
      </div>
    </div>
  );
}
